import type { ReactNode } from 'react';
import { Link } from 'react-router';
import { LIBELLE_ETAPE, LIBELLE_STATUT_CONGE, LIBELLE_STATUT_CONSULTATION, LIBELLE_STATUT_PRET, LIBELLE_STATUT_PRET_ACCORDE, type BlocProcedures, type BlocRh, type ControleConge, type DevisePret, type Etape, type ResumePeriode, type StatutConge, type StatutConsultation, type StatutPret, type StatutPretAccorde } from '@/api/types';
import { Kpi } from '@/components/common';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { cn, dateFr } from '@/lib/utils';

const VARIANTE_STATUT: Record<StatutConge, 'default' | 'secondary' | 'destructive' | 'outline'> = {
  BROUILLON: 'outline',
  SOUMIS: 'secondary',
  A_CORRIGER: 'destructive',
  APPROUVE: 'default',
  REJETE: 'destructive',
  RETIRE: 'outline',
  ANNULE: 'outline',
  INTERROMPU: 'secondary',
  PRIS: 'default',
  REMPLACE: 'outline',
};

export const StatutCongeBadge = ({ statut }: { statut: StatutConge }) => <Badge variant={VARIANTE_STATUT[statut]}>{LIBELLE_STATUT_CONGE[statut]}</Badge>;

const VARIANTE_STATUT_PRET: Record<StatutPret, 'default' | 'secondary' | 'destructive' | 'outline'> = {
  PLAFOND_DEMANDE: 'outline',
  PLAFOND_COMMUNIQUE: 'secondary',
  SIMULATION: 'secondary',
  SOUMIS: 'secondary',
  A_CORRIGER: 'destructive',
  SUSPENDU: 'outline',
  APPROUVE: 'default',
  REJETE: 'destructive',
  ANNULE: 'outline',
  DECAISSE: 'default',
  EN_REMBOURSEMENT: 'default',
  SOLDE: 'outline',
};
export const StatutPretBadge = ({ statut }: { statut: StatutPret }) => <Badge variant={VARIANTE_STATUT_PRET[statut]}>{LIBELLE_STATUT_PRET[statut]}</Badge>;

const VARIANTE_STATUT_PRET_ACCORDE: Record<StatutPretAccorde, 'default' | 'secondary' | 'destructive' | 'outline'> = { A_DECAISSER: 'secondary', DECAISSE: 'default', EN_REMBOURSEMENT: 'default', REAMENAGE: 'secondary', EN_RECOUVREMENT: 'destructive', SOLDE: 'outline' };
export const StatutPretAccordeBadge = ({ statut }: { statut: StatutPretAccorde }) => <Badge variant={VARIANTE_STATUT_PRET_ACCORDE[statut]}>{LIBELLE_STATUT_PRET_ACCORDE[statut]}</Badge>;

/** Montant dans la devise du prêt (USD ou CDF), deux décimales, séparateur de milliers lisible. */
export const montantDevise = (n: number | null | undefined, devise: DevisePret | string): string => (n === null || n === undefined ? '—' : `${n.toLocaleString('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).replace(/\u202f/g, '\u00a0')}\u00a0${devise}`);

/** Solde actuel → Jours demandés → Solde prévisionnel (spécifications démarches RH, § 4.4). */
export function BlocSolde({ periode, demandes, apres, titre = 'Congé annuel' }: { periode: ResumePeriode | null; demandes: number; apres: number | null; titre?: string }) {
  if (!periode) return <p className="text-sm text-muted-foreground">Aucune période de droits ouverte : le droit au congé annuel s'ouvre après douze mois de service.</p>;
  const negatif = apres !== null && apres < 0;
  return (
    <div className="space-y-2" data-testid="bloc-solde">
      <div className="text-sm text-muted-foreground">
        {titre} · période du {dateFr(periode.debut)} au {dateFr(periode.fin)} ({periode.droits} jours ouvrables de droits : {periode.joursBase} + {periode.joursMajoration} d'ancienneté + {periode.joursReportRecu} reportés{periode.regularisations ? ` ${periode.regularisations > 0 ? '+' : ''}${periode.regularisations} régularisés` : ''})
      </div>
      <div className="grid gap-3 sm:grid-cols-3">
        <div className="rounded-lg border bg-card p-4">
          <div className="text-xs uppercase text-muted-foreground">Solde actuel</div>
          <div className="num text-xl font-semibold">{periode.disponible} j</div>
          <div className="text-xs text-muted-foreground">
            {periode.pris} pris · {periode.approuves} approuvés · {periode.enAttente} en attente
          </div>
        </div>
        <div className="rounded-lg border bg-card p-4">
          <div className="text-xs uppercase text-muted-foreground">Jours demandés</div>
          <div className="num text-xl font-semibold">{demandes} j</div>
        </div>
        <div className={cn('rounded-lg border p-4', negatif ? 'border-red-300 bg-red-50' : 'bg-emerald-50')}>
          <div className="text-xs uppercase text-muted-foreground">Solde après approbation</div>
          <div className="num text-xl font-semibold">{apres === null ? '—' : `${apres} j`}</div>
          {periode.alerteExpiration && <div className="text-xs text-amber-700">Droits non pris perdus après le {dateFr(periode.fin)} (report limité à une année de droits).</div>}
        </div>
      </div>
    </div>
  );
}

/** Étapes prévues ou suivies du circuit d'une demande. */
export function EtapesPrevues({ etapes, courante }: { etapes: Etape[]; courante?: Etape | null }) {
  if (!etapes.length) return <span className="text-sm text-muted-foreground">Hors circuit applicatif : enregistrement par les Ressources humaines.</span>;
  return (
    <ol className="flex flex-wrap items-center gap-1.5 text-sm">
      {etapes.map((e, i) => (
        <li key={e} className="flex items-center gap-1.5">
          <Badge variant={e === courante ? 'default' : 'outline'}>{LIBELLE_ETAPE[e]}</Badge>
          {i < etapes.length - 1 && <span className="text-muted-foreground">→</span>}
        </li>
      ))}
    </ol>
  );
}

export function ControlesConge({ controles }: { controles: ControleConge[] }) {
  if (!controles.length) return null;
  return (
    <ul className="space-y-1 text-sm">
      {controles.map((c, i) => (
        <li key={i} className={cn('flex items-start gap-2', c.ok ? 'text-emerald-700' : c.bloquant === false ? 'text-amber-700' : 'text-red-700')}>
          <span aria-hidden>{c.ok ? '✓' : c.bloquant === false ? '!' : '✗'}</span>
          <span>{c.message}</span>
        </li>
      ))}
    </ul>
  );
}

/** Carte d'une période de droits (situation de l'agent). */
export function CartePeriode({ p, titre, enfants }: { p: ResumePeriode; titre?: ReactNode; enfants?: ReactNode }) {
  return (
    <div className={cn('rounded-lg border p-4', p.cloturee ? 'bg-muted/40' : 'bg-card')} data-testid="periode">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="font-medium">
          {titre ?? `Période du ${dateFr(p.debut)} au ${dateFr(p.fin)}`} {p.cloturee && <Badge variant="outline">clôturée</Badge>} {p.origine === 'REPRISE' && <Badge variant="outline">reprise</Badge>}
        </div>
        <div className="num text-2xl font-semibold">{p.cloturee ? `${p.reportSortant ?? 0} j reportés` : `${p.disponible} j`}</div>
      </div>
      <div className="mt-2 grid gap-1 text-sm text-muted-foreground sm:grid-cols-2">
        <div>Droits : {p.droits} = {p.joursBase} + {p.joursMajoration} d'ancienneté ({p.anneesAnciennete} ans) + {p.joursReportRecu} reportés{p.regularisations ? ` ${p.regularisations > 0 ? '+' : ''}${p.regularisations} régularisés` : ''}</div>
        <div>Pris {p.pris} · approuvés à venir {p.approuves} · en attente {p.enAttente}</div>
        {p.cloturee ? (
          <div>
            Clôturée le {dateFr(p.fin)} : {p.reportSortant ?? 0} jour{(p.reportSortant ?? 0) > 1 ? 's' : ''} reporté{(p.reportSortant ?? 0) > 1 ? 's' : ''} sur la période suivante, {p.perte ?? 0} perdu{(p.perte ?? 0) > 1 ? 's' : ''}
          </div>
        ) : (
          <div>Date limite d'utilisation : {dateFr(p.fin)}{p.joursAvantFin >= 0 ? ` (dans ${p.joursAvantFin} jours)` : ''}</div>
        )}
        {p.alerteExpiration && <div className="text-amber-700">Des jours disponibles expirent bientôt : au-delà d'une année de droits reportable, ils seront perdus.</div>}
      </div>
      {enfants}
    </div>
  );
}

export const LienConge = ({ id, numero }: { id: string; numero: string | null }) => (
  <Link to={`/rh/conges/${id}`} className="font-medium text-primary hover:underline">
    {numero ?? 'Brouillon'}
  </Link>
);

/** Bloc « Mes démarches RH » du tableau de bord (§ 7.2) : solde, prochaine absence, demandes en cours, congés à traiter, compteurs RH. */
export function BlocRhTableau({ bloc }: { bloc: BlocRh }) {
  return (
    <Card data-testid="bloc-rh">
      <CardHeader>
        <CardTitle className="flex flex-wrap items-center justify-between gap-2">
          Mes démarches RH
          <Button asChild size="sm" variant="outline">
            <Link to="/rh">Ouvrir mon espace</Link>
          </Button>
        </CardTitle>
        <CardDescription>Congés : solde disponible, prochaine absence, demandes en cours et, selon votre profil, demandes à traiter.</CardDescription>
      </CardHeader>
      <CardContent className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        <Link to="/rh">
          <Kpi label="Solde de congé annuel" valeur={bloc.solde === null ? '—' : `${bloc.solde} j`} detail={bloc.dateLimite ? `à utiliser avant le ${dateFr(bloc.dateLimite)}` : 'période non ouverte'} accent={bloc.alerteExpiration ? 'alerte' : undefined} />
        </Link>
        <Link to={bloc.prochaineAbsence ? `/rh/conges/${bloc.prochaineAbsence.id}` : '/rh/conges/nouvelle'}>
          <Kpi label="Prochaine absence" valeur={bloc.prochaineAbsence ? dateFr(bloc.prochaineAbsence.debut) : 'aucune'} detail={bloc.prochaineAbsence ? `${bloc.prochaineAbsence.libelle} · reprise le ${dateFr(bloc.prochaineAbsence.reprise)}` : 'nouvelle demande de congé'} />
        </Link>
        <Link to="/rh/demandes">
          <Kpi label="Mes demandes en cours" valeur={bloc.demandesEnCours} detail="en circuit de validation" />
        </Link>
        {bloc.prets && <Link to={bloc.prets.demande ? `/rh/prets/${bloc.prets.demande.id}` : '/rh/prets/nouvelle'}>
          <Kpi label="Prêt social" valeur={bloc.prets.demande ? bloc.prets.demande.libelleStatut : bloc.prets.eligible ? 'aucune demande' : 'non éligible'} detail={bloc.prets.demande ? `${bloc.prets.demande.numero ?? ''}${bloc.prets.demande.conditionsEnAttente ? ' · conditions à accepter' : bloc.prets.demande.etape ? ` · ${LIBELLE_ETAPE[bloc.prets.demande.etape]}` : ''}${bloc.prets.plafond ? ` · plafond ${montantDevise(bloc.prets.plafond.montant, bloc.prets.plafond.devise)}${bloc.prets.plafond.expire ? ' (expiré)' : ''}` : ''}` : bloc.prets.eligible ? 'demander un prêt' : 'douze mois de service requis'} accent={bloc.prets.demande?.conditionsEnAttente || bloc.prets.plafond?.expire ? 'alerte' : undefined} />
        </Link>}
        {bloc.prets?.pret && (
          <Link to={bloc.prets.pret.demandeId ? `/rh/prets/${bloc.prets.pret.demandeId}?onglet=remboursement` : `/rh/portefeuille/${bloc.prets.pret.id}`}>
            <Kpi label={`Prêt ${bloc.prets.pret.numero}`} valeur={montantDevise(bloc.prets.pret.capitalRestant, bloc.prets.pret.devise)} detail={`${bloc.prets.pret.libelleStatut}${bloc.prets.pret.prochaineEcheance ? ` · prochaine échéance le ${dateFr(bloc.prets.pret.prochaineEcheance.date)} (${montantDevise(bloc.prets.pret.prochaineEcheance.mensualite, bloc.prets.pret.devise)})` : ''}${bloc.prets.pret.nonRetenues ? ` · ${bloc.prets.pret.nonRetenues} non retenue(s)` : ''}`} accent={bloc.prets.pret.nonRetenues ? 'alerte' : undefined} />
          </Link>
        )}
        {bloc.prets?.portefeuille && (
          <Link to="/rh/portefeuille">
            <Kpi label="Portefeuille des prêts" valeur={bloc.prets.portefeuille.pretsEnForce} detail={`encours ${montantDevise(bloc.prets.portefeuille.encours, 'USD')} · ${bloc.prets.portefeuille.aDecaisser} à décaisser · ${bloc.prets.portefeuille.nonRetenues} non retenue(s) · ${bloc.prets.portefeuille.recouvrements} en recouvrement`} accent={bloc.prets.portefeuille.nonRetenues + bloc.prets.portefeuille.recouvrements > 0 ? 'alerte' : undefined} />
          </Link>
        )}
        {bloc.prets && bloc.prets.plafondsACalculer !== null && (
          <Link to="/rh/prets?onglet=plafonds">
            <Kpi label="Plafonds de prêt à vérifier" valeur={bloc.prets.plafondsACalculer} detail="décompte final et plafond à communiquer" accent={bloc.prets.plafondsACalculer > 0 ? 'alerte' : undefined} />
          </Link>
        )}
        {bloc.prets && bloc.prets.aTraiter > 0 && (
          <Link to="/rh/prets?onglet=a-traiter">
            <Kpi label="Dossiers de prêt à traiter" valeur={bloc.prets.aTraiter} detail="avis, analyse ou décision attendus" accent="alerte" />
          </Link>
        )}
        {bloc.rh ? (
          <Link to="/rh/conges/droits">
            <Kpi label="Ressources humaines" valeur={bloc.rh.regimesAConfirmer + bloc.rh.aEnregistrer + bloc.rh.interruptions} detail={`${bloc.rh.regimesAConfirmer} régime(s) à confirmer · ${bloc.rh.aEnregistrer} décision(s) à enregistrer · ${bloc.rh.interruptions} interruption(s)`} accent={bloc.rh.regimesAConfirmer + bloc.rh.aEnregistrer + bloc.rh.interruptions > 0 ? 'alerte' : undefined} />
          </Link>
        ) : (
          <Link to="/rh/conges?onglet=a-traiter">
            <Kpi label="Congés à traiter" valeur={bloc.aTraiter} detail="en attente de votre décision" accent={bloc.aTraiter > 0 ? 'alerte' : undefined} />
          </Link>
        )}
      </CardContent>
    </Card>
  );
}

// ------------------------------------------------------------------ Bibliothèque des procédures (lot RH-5)
const VARIANTE_STATUT_CONSULTATION: Record<StatutConsultation, 'default' | 'secondary' | 'destructive' | 'outline'> = { A_CONSULTER: 'secondary', LU: 'outline', A_RELIRE: 'destructive', CONFIRMATION_ATTENDUE: 'default', CONFIRMEE: 'outline' };
export const StatutConsultationBadge = ({ statut }: { statut: StatutConsultation }) => (
  <Badge variant={VARIANTE_STATUT_CONSULTATION[statut]} className={cn((statut === 'LU' || statut === 'CONFIRMEE') && 'border-emerald-300 bg-emerald-50 text-emerald-800')}>
    {LIBELLE_STATUT_CONSULTATION[statut]}
  </Badge>
);

/** Bloc du tableau de bord (§ 6.3) : quatre indicateurs cliquables, puis les compteurs de gestion et de suivi selon les droits. */
export function BlocProceduresTableau({ bloc }: { bloc: BlocProcedures }) {
  return (
    <Card data-testid="bloc-procedures">
      <CardHeader>
        <CardTitle className="flex flex-wrap items-center justify-between gap-2">
          Bibliothèque des procédures
          <Button asChild size="sm" variant="outline">
            <Link to="/procedures">Ouvrir la bibliothèque</Link>
          </Button>
        </CardTitle>
        <CardDescription>Procédures qui vous sont applicables : à consulter, lues, nouvelles versions à relire, confirmations attendues. Chaque indicateur ouvre la liste correspondante.</CardDescription>
      </CardHeader>
      <CardContent className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        <Link to="/procedures?statut=A_CONSULTER">
          <Kpi label="Procédures à consulter" valeur={bloc.aConsulter} detail="jamais ouvertes dans le lecteur" accent={bloc.aConsulter > 0 ? 'alerte' : 'ok'} />
        </Link>
        <Link to="/rh/procedures">
          <Kpi label="Procédures lues" valeur={bloc.lues} detail="version en vigueur consultée" />
        </Link>
        <Link to="/procedures?statut=A_RELIRE">
          <Kpi label="Nouvelles versions" valeur={bloc.nouvellesVersions} detail="à relire" accent={bloc.nouvellesVersions > 0 ? 'alerte' : undefined} />
        </Link>
        <Link to="/procedures?statut=CONFIRMATION_ATTENDUE">
          <Kpi label="Confirmations attendues" valeur={bloc.confirmationsAttendues} detail="prise de connaissance à confirmer" accent={bloc.confirmationsAttendues > 0 ? 'alerte' : undefined} />
        </Link>
        {bloc.actions.gerer && (
          <Link to="/procedures/gestion?onglet=a-valider">
            <Kpi label="Procédures à valider" valeur={bloc.aValider} detail={`${bloc.enRedaction} en préparation ou en validation`} accent={bloc.aValider > 0 ? 'alerte' : undefined} />
          </Link>
        )}
        {bloc.suivi && (
          <Link to="/procedures/suivi">
            <Kpi label="Suivi des consultations" valeur={bloc.suivi.tauxConsultation === null ? '—' : `${bloc.suivi.tauxConsultation.toLocaleString('fr-FR')} %`} detail={`${bloc.suivi.publiees} procédure(s) publiée(s) · taux de consultation`} />
          </Link>
        )}
      </CardContent>
    </Card>
  );
}
